import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import SlideOver from "@/components/SlideOver";
import UpgradeModal from "@/components/UpgradeModal";
import { Mic, Square, ImagePlus, Video, Loader2, Trash2, Sparkles, Gift, Check, MessageCircle, Crown } from "lucide-react";

const KIND_ICON = { voice: Mic, video: Video, photo: ImagePlus };

/**
 * The Living Capsule — add voice/video/photo updates to a card AFTER it's been sent. The
 * card's existing QR/Sovereign Seal remains the permanent key; recipients see new updates the
 * next time they scan it (Experience Hub). Boutique + 10-Card Pack: 1 update/card. 25/50-Card
 * Pack & Business Team: unlimited.
 *
 * Living Cash — Root Admin (and any Premium user) can ALSO add/update a cash gift on an
 * already-sent card, wired to the existing PUT /cards/{id}/gift endpoint (same one used
 * pre-send in the Design Studio) — no new backend endpoint needed.
 *
 * Reciprocity — this modal is the sender's command center for THIS card, so it also surfaces
 * any Maison Replies the recipient has sent back for it (GET /replies?card_id=).
 */
const LivingCapsuleModal = ({ open, onClose, card }) => {
  const [updates, setUpdates] = useState([]);
  const [allowance, setAllowance] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [caption, setCaption] = useState("");
  const [recording, setRecording] = useState(false);
  const [saving, setSaving] = useState(false);
  const [upgradeGate, setUpgradeGate] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const photoRef = useRef(null);
  const videoRef = useRef(null);
  // Living Cash
  const [cashAmount, setCashAmount] = useState(25);
  const [cashGift, setCashGift] = useState(null);
  const [cashSaving, setCashSaving] = useState(false);
  const [cashSaved, setCashSaved] = useState(false);
  const [cashError, setCashError] = useState("");
  // Reciprocity — replies tied to this specific card
  const [cardReplies, setCardReplies] = useState([]);

  const load = async () => {
    if (!card) return;
    setLoading(true);
    try {
      const [capRes, repliesRes] = await Promise.all([
        api.get(`/cards/${card.id}/capsule`),
        api.get("/replies", { params: { card_id: card.id } }),
      ]);
      setUpdates(capRes.data.updates || []);
      setAllowance(capRes.data.allowance);
      setRemaining(capRes.data.remaining);
      setCardReplies(repliesRes.data.replies || []);
    } catch (err) {
      console.error("Living Capsule load failed:", err);
      setError(err?.response?.data?.detail || "Could not load the Capsule.");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (open && card) {
      load();
      setCashGift(card.cash_gift || null);
      setCashAmount(card.cash_gift?.amount || 25);
      setCashSaved(false); setCashError("");
    }
  }, [open, card]); // eslint-disable-line react-hooks/exhaustive-deps

  const addCash = async () => {
    if (!card || !cashAmount || cashAmount <= 0) return;
    setCashSaving(true); setCashError("");
    try {
      const res = await api.put(`/cards/${card.id}/gift`, { gift_type: "cash", amount: parseFloat(cashAmount) });
      // Living Cash — a real Stripe charge is required before the gift is real. Redirect out;
      // Stripe's success_url routes back to /cards which finalizes + shows it funded there.
      if (res.data.mode === "live" && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setCashGift(res.data.cash_gift);
      setCashSaved(true);
      setTimeout(() => setCashSaved(false), 2500);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "upgrade_required") setUpgradeGate(true);
      else setCashError((d && typeof d === "object" ? d.message : d) || "Could not add the cash gift.");
    } finally { setCashSaving(false); }
  };

  const submitUpdate = async (kind, content_base64, mime) => {
    setSaving(true); setError("");
    try {
      await api.post(`/cards/${card.id}/capsule`, { kind, content_base64, mime, caption });
      setCaption("");
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "upgrade_required") setUpgradeGate(true);
      else setError(typeof d === "string" ? d : "Could not add the update.");
    } finally { setSaving(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(blob); });
        await submitUpdate("voice", b64, "audio/webm");
      };
      mediaRecRef.current = mr; mr.start(); setRecording(true);
      setTimeout(() => { if (mediaRecRef.current?.state === "recording") mediaRecRef.current.stop(); }, 30000);
    } catch (e) { setError("Microphone access is required to record."); }
  };
  const stopRecording = () => { if (mediaRecRef.current?.state === "recording") mediaRecRef.current.stop(); setRecording(false); };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });
    await submitUpdate("photo", b64, file.type || "image/jpeg");
    if (photoRef.current) photoRef.current.value = "";
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setError("Keep video clips under ~8MB (~20-30s) — no cloud video storage is configured yet."); return; }
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });
    await submitUpdate("video", b64, file.type || "video/webm");
    if (videoRef.current) videoRef.current.value = "";
  };

  const removeUpdate = async (id) => {
    await api.delete(`/cards/${card.id}/capsule/${id}`);
    await load();
  };

  const canAdd = allowance === null || (remaining ?? 0) > 0;

  return (
    <SlideOver open={open} onClose={onClose} title="The Living Capsule" subtitle={card ? `${card.loved_one_name} · ${card.occasion}` : ""}>
      {loading ? (
        <div className="text-center py-12 text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-5">
          <div className="surface rounded-xl px-3 py-2 text-xs text-muted-foreground flex items-center justify-between" data-testid="capsule-allowance">
            <span>Updates on this card</span>
            <span className="gold-text font-medium">{allowance === null ? "Unlimited" : `${remaining} of ${allowance} remaining`}</span>
          </div>

          {error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</div>}

          <div className="surface rounded-2xl p-4 space-y-2">
            <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="A short caption (optional)" data-testid="capsule-caption"
              className="w-full bg-input border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
            <div className="flex gap-2">
              {!recording ? (
                <button data-testid="capsule-record-btn" onClick={() => (canAdd ? startRecording() : setUpgradeGate(true))} disabled={saving}
                  className="flex-1 text-sm flex items-center justify-center gap-1.5 surface rounded-xl py-2.5 hover:border-primary/40 disabled:opacity-60"><Mic className="w-4 h-4" /> Voice</button>
              ) : (
                <button onClick={stopRecording} className="flex-1 text-sm flex items-center justify-center gap-1.5 bg-red-500/15 text-red-500 rounded-xl py-2.5"><Square className="w-4 h-4" /> Stop</button>
              )}
              <button data-testid="capsule-photo-btn" onClick={() => (canAdd ? photoRef.current?.click() : setUpgradeGate(true))} disabled={saving}
                className="flex-1 text-sm flex items-center justify-center gap-1.5 surface rounded-xl py-2.5 hover:border-primary/40 disabled:opacity-60"><ImagePlus className="w-4 h-4" /> Photo</button>
              <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
              <button data-testid="capsule-video-btn" onClick={() => (canAdd ? videoRef.current?.click() : setUpgradeGate(true))} disabled={saving}
                className="flex-1 text-sm flex items-center justify-center gap-1.5 surface rounded-xl py-2.5 hover:border-primary/40 disabled:opacity-60"><Video className="w-4 h-4" /> Video</button>
              <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={uploadVideo} />
            </div>
            {saving && <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding + notifying…</p>}
            {!canAdd && <p className="text-xs text-center gold-text">Buy a 25/50-Card Pack or go Business for unlimited updates.</p>}
          </div>

          {/* Living Cash — add/update a cash gift on this already-sent card */}
          <div className="surface rounded-2xl p-4 space-y-2" data-testid="living-cash-section">
            <h4 className="text-sm font-medium flex items-center gap-1.5"><Gift className="w-3.5 h-3.5 gold-text" /> Add Cash to this card</h4>
            {cashGift?.amount ? (
              <p className="text-xs text-muted-foreground">Currently attached: <span className="gold-text font-medium">${Number(cashGift.amount).toFixed(2)}</span> — update the amount below to change it.</p>
            ) : (
              <p className="text-xs text-muted-foreground">Send a little extra, even after the card&apos;s already out — recipients see it next time they open their Experience Hub.</p>
            )}
            <div className="flex gap-2">
              <input type="number" min="1" data-testid="living-cash-amount" value={cashAmount} onChange={(e) => setCashAmount(e.target.value)}
                className="flex-1 bg-input border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Amount $" />
              <button data-testid="living-cash-btn" onClick={addCash} disabled={cashSaving}
                className="text-sm flex items-center justify-center gap-1.5 bg-gradient-to-r from-secondary to-primary text-white rounded-xl px-4 py-2 hover:opacity-90 disabled:opacity-60">
                {cashSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : cashSaved ? <><Check className="w-4 h-4" /> Saved</> : <>Add Cash</>}
              </button>
            </div>
            {cashError && <p className="text-xs text-red-400">{cashError}</p>}
          </div>

          {/* Reciprocity — replies the recipient has sent back for THIS card */}
          {cardReplies.length > 0 && (
            <div className="surface rounded-2xl p-4 space-y-2" data-testid="living-capsule-replies">
              <h4 className="text-sm font-medium flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5 gold-text" /> Replies to this card</h4>
              <div className="space-y-2">
                {cardReplies.map((r) => (
                  <div key={r.id} className="bg-muted/30 rounded-xl p-3" data-testid="living-capsule-reply-item">
                    <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5 flex-wrap">
                      <span>{r.from_name}</span>
                      {r.new_resident && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full"
                          style={{ background: "linear-gradient(135deg, #f6e3a3 0%, #c9a24a 50%, #8a6a2a 100%)", color: "#3d2e0f" }}>
                          <Crown className="w-2.5 h-2.5" /> New Resident
                        </span>
                      )}
                    </div>
                    {r.message && <p className="text-sm italic">&ldquo;{r.message}&rdquo;</p>}
                    {r.kind === "voice" && r.audio_base64 && (
                      <audio controls className="w-full mt-1.5" src={`data:${r.audio_mime || "audio/webm"};base64,${r.audio_base64}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 gold-text" /> Capsule feed</h4>
            {updates.length === 0 ? <p className="text-sm text-muted-foreground">No updates yet.</p> : (
              <div className="space-y-2" data-testid="capsule-updates-list">
                {updates.map((u) => {
                  const Icon = KIND_ICON[u.kind] || Sparkles;
                  return (
                    <div key={u.id} className="surface rounded-xl p-3 flex items-center gap-3" data-testid="capsule-update-item">
                      <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-primary" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">{u.caption || u.kind}</div>
                        <div className="text-[11px] text-muted-foreground">{(u.created_at || "").slice(0, 10)}</div>
                      </div>
                      <button onClick={() => removeUpdate(u.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      <UpgradeModal open={upgradeGate} onClose={() => setUpgradeGate(false)} tier="credit_pack" feature="Unlimited Living Updates" />
    </SlideOver>
  );
};

export default LivingCapsuleModal;
